import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { EMPTY, Observable, switchMapTo, timer } from 'rxjs';

export const provideDuplicateBetplacementInterceptor: () => HttpInterceptorFn = () => {
    const requestIds = new Set<string>();

    return (req, next) => intercept(req, next, requestIds);
};

function intercept(req: HttpRequest<unknown>, next: HttpHandlerFn, requestIds: Set<string>): Observable<HttpEvent<unknown>> {
    const betslipConfig = inject(BetslipConfig);
    const logger = inject(LoggerFactory).getLogger('DuplicateBetplacementInterceptor');

    if (!betslipConfig.preventDuplicateRequestOnFrontend) {
        return next(req);
    }

    if (req.method === 'POST' && isBetplacementRequest(req)) {
        const request = req.clone({ url: `${req.url}?nonce=${generateGuid()}` });
        const requestId = getRequestId(req);

        if (!requestId) {
            const errMessage = `Unable to find requestId field for endpoint ${request.url} - has the request format changed?`;
            logger.error(new Error(errMessage));

            console.error(errMessage);

            return next(request); //since we couldn't find a request id - we should let the request go through
        }

        if (requestIds.has(requestId)) {
            logger.error(new Error(`Trying to place a bet to: ${request.url} with the same id: ${requestId}`));

            // switchmap to EMPTY will make the promise never resolve/reject
            return timer(1).pipe(switchMapTo(EMPTY));
        }

        requestIds.add(requestId);

        return next(request);
    }

    return next(req);
}

function isBetplacementRequest(req: HttpRequest<unknown>) {
    return req.url.endsWith('/api/placebet') || req.url.endsWith('/api/placebetv2');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRequestId(req: HttpRequest<any>): string {
    return req.body.requestId || req.body.betslip?.requestId;
}
